class StoreAvailability extends HTMLElement {
  #ac               = null;
  #data             = {};
  #currentVariant   = null;
  #drawer           = null;
  #onVariantChanged = null;
  #trap             = null;

  #onKeydown = (e) => {
    if (e.key === 'Escape' && this.#isOpen()) this.#close();
  };

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;

    // Teleport drawer to <body> so it escapes any stacking context from the
    // product section and sits at the root z-index level (same as cart-drawer).
    this.#drawer = this.querySelector('.store-availability__drawer');
    document.body.appendChild(this.#drawer);

    this.addEventListener('click', this.#onClick.bind(this), { signal });
    this.#drawer.addEventListener('click', this.#onClick.bind(this), { signal });
    document.addEventListener('keydown', this.#onKeydown);

    this.#onVariantChanged = ({ detail }) => {
      this.#currentVariant = String(detail.variantId);
      this.#loadVariant(this.#currentVariant);
    };
    document.addEventListener('variant:changed', this.#onVariantChanged);

    this.#currentVariant = this.dataset.currentVariant ?? null;
    if (this.#currentVariant) {
      this.#loadVariant(this.#currentVariant);
    } else {
      // No variant to ask about — render the empty state rather than leaving
      // the (now always visible) trigger opening a drawer with no explanation.
      this.#renderList(null);
    }
  }

  disconnectedCallback() {
    this.#ac?.abort();
    this.#trap?.release({ restoreFocus: false });
    document.removeEventListener('variant:changed', this.#onVariantChanged);
    document.removeEventListener('keydown', this.#onKeydown);
    this.#drawer?.remove();
  }

  /** @param {MouseEvent} e */
  #onClick(e) {
    if (e.target.closest('[data-trigger]')) { this.#open();  return; }
    if (e.target.closest('[data-close]'))   { this.#close(); }
  }

  async #loadVariant(variantId) {
    if (this.#data[variantId] !== undefined) {
      this.#renderList(variantId);
      return;
    }

    const token      = this.dataset.storefrontToken;
    const shopDomain = this.dataset.shopDomain;
    const trigger    = this.querySelector('[data-trigger]');

    if (!shopDomain || !token) { trigger.hidden = true; return; }

    try {
      const res = await fetch(
        `https://${shopDomain}/api/2024-01/graphql.json`,
        {
          method: 'POST',
          signal: this.#ac.signal,
          headers: {
            'Content-Type': 'application/json',
            'X-Shopify-Storefront-Access-Token': token,
          },
          body: JSON.stringify({
            query: `query StoreAvailability($id: ID!) {
              node(id: $id) {
                ... on ProductVariant {
                  storeAvailability(first: 20) {
                    nodes {
                      available
                      quantityAvailable
                      location {
                        id
                        name
                        address {
                          address1
                          zip
                          city
                        }
                      }
                    }
                  }
                }
              }
            }`,
            variables: { id: `gid://shopify/ProductVariant/${variantId}` },
          }),
        }
      );

      if (res.ok) {
        const json = await res.json();
        if (json.errors?.length) console.warn('[store-availability] GraphQL errors:', json.errors);
        this.#data[variantId] = json?.data?.node?.storeAvailability?.nodes ?? [];
      } else {
        this.#data[variantId] = [];
      }
    } catch (err) {
      if (err.name === 'AbortError') return;
      this.#data[variantId] = [];
    }

    // A location with no numeric quantity can never be shown as in stock, so a
    // response where none of them carry one almost certainly means the
    // Storefront token is missing the unauthenticated_read_product_inventory
    // scope — which would otherwise degrade silently to "empty in every store".
    const nodes = this.#data[variantId];
    if (nodes.length > 0 && !nodes.some(l => typeof l.quantityAvailable === 'number')) {
      console.warn('[store-availability] no numeric quantityAvailable — check the storefront token inventory scope');
    }

    if (this.#currentVariant !== variantId) return;
    this.#renderList(variantId);
  }

  /**
   * Locations holding the variant in stock. A location is only in stock with a
   * known, positive quantity — pickup-eligible with zero on hand ("can be
   * ordered here") is not stock, and an unknown quantity is not a known one.
   * @param {string} variantId
   */
  #inStock(variantId) {
    return (this.#data[variantId] ?? []).filter(
      l => l.available === true
        && typeof l.quantityAvailable === 'number'
        && l.quantityAvailable > 0
    );
  }

  #renderList(variantId) {
    const list      = this.#drawer.querySelector('[data-list]');
    const empty     = this.#drawer.querySelector('[data-empty]');
    const subtitle  = this.#drawer.querySelector('.store-availability__subtitle');
    const locations = this.#inStock(variantId);

    list.innerHTML = '';
    // "Disse butikkene har varen på lager" reads as a lie above an empty list.
    empty.hidden    = locations.length > 0;
    subtitle.hidden = locations.length === 0;

    for (const loc of locations) {
      const qty         = loc.quantityAvailable;
      const isLow       = qty <= 2;
      const isHighStock = qty > 10;

      const li = document.createElement('li');
      li.className = [
        'store-availability__location',
        isLow ? 'store-availability__location--low' : '',
      ].filter(Boolean).join(' ');

      const content = document.createElement('div');
      content.className = 'store-availability__location-content';

      const name = document.createElement('p');
      name.className = 'store-availability__location-name';
      name.textContent = loc.location?.name ?? '';

      const addr = document.createElement('p');
      addr.className = 'store-availability__location-address';
      const a = loc.location?.address ?? {};
      addr.textContent = [a.address1, a.zip || a.city ? `${a.zip ?? ''} ${a.city ?? ''}`.trim() : null].filter(Boolean).join(', ');

      content.appendChild(name);
      content.appendChild(addr);

      const stock = document.createElement('div');
      stock.className = 'store-availability__stock';

      const dot = document.createElement('span');
      dot.className = 'store-availability__stock-dot';
      dot.setAttribute('aria-hidden', 'true');

      const label = document.createElement('span');
      label.className = 'store-availability__stock-label';
      if (isLow) {
        label.textContent = (this.dataset.i18nLow ?? '').replace('%count%', qty);
      } else if (isHighStock) {
        label.textContent = this.dataset.i18nHighStock;
      } else {
        label.textContent = (this.dataset.i18nInStock ?? '').replace('%count%', qty);
      }

      stock.appendChild(dot);
      stock.appendChild(label);

      li.appendChild(content);
      li.appendChild(stock);
      list.appendChild(li);
    }
  }

  #open() {
    if (this.#drawer.classList.contains('is-open')) return;
    this.#drawer.classList.add('is-open');
    this.#drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    this.#trap = new FocusTrap(this.#drawer);
    this.#trap.activate(this.#drawer.querySelector('.store-availability__close'));
  }

  #close() {
    this.#drawer.classList.remove('is-open');
    this.#drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
    this.#trap?.release({ restoreFocus: false });
    this.#trap = null;
    this.querySelector('[data-trigger]')?.focus();
  }

  #isOpen() {
    return this.#drawer?.classList.contains('is-open') ?? false;
  }
}

customElements.define('store-availability', StoreAvailability);
